import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiUserPlus, FiTrash2, FiShield, FiUser, FiLock, FiCheck, FiX, FiUsers, FiRefreshCw, FiClock } from "react-icons/fi";
import { useDialog, Dialog } from "./Dialog";
import "./AdminDashboard.css";

const API_URL = "";

const AdminDashboard = ({ user }) => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", role: "admin" });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });

  const [registrations, setRegistrations] = useState([]);
  const [regsLoading, setRegsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("registrations");
  const [regFilter, setRegFilter] = useState("all");

  const { dialogState, confirm, alert } = useDialog();
  const isSuperAdmin = user && user.role === "super_admin";
  const isAdmin = user && (user.role === "super_admin" || user.role === "admin");

  useEffect(() => {
    if (isSuperAdmin) fetchAdmins();
    if (isAdmin) fetchRegistrations();
  }, [isSuperAdmin, isAdmin]);

  async function fetchAdmins() {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/api/auth/admins`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setAdmins(res.data);
    } catch (err) {
      console.error("Error fetching admins:", err);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchRegistrations() {
    try {
      setRegsLoading(true);
      const res = await axios.get(`${API_URL}/api/tournaments/all-requests`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setRegistrations(res.data);
    } catch (err) {
      console.error("Error fetching registrations:", err);
    } finally {
      setRegsLoading(false);
    }
  }

  async function handleAddAdmin(e) {
    e.preventDefault();
    if (newAdmin.password.length < 6) {
      await alert("Password must be at least 6 characters.");
      return;
    }
    try {
      await axios.post(`${API_URL}/api/auth/admins`, newAdmin, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setNewAdmin({ username: "", password: "", role: "admin" });
      setShowAddForm(false);
      fetchAdmins();
      await alert("Admin created successfully!");
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to create admin");
    }
  }

  async function handleDeleteAdmin(username) {
    const ok = await confirm(`Delete admin "${username}"? This cannot be undone.`, {
      title: "Delete Admin",
      confirmText: "Delete",
      dangerous: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api/auth/admins/${username}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      fetchAdmins();
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to delete admin");
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      await alert("New passwords don't match.");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      await alert("New password must be at least 6 characters.");
      return;
    }
    try {
      await axios.put(
        `${API_URL}/api/auth/change-password`,
        { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setShowPasswordForm(false);
      await alert("Password changed successfully!");
    } catch (err) {
      await alert(err.response?.data?.message || "Failed to change password");
    }
  }

  async function handleRequestAction(tournamentId, discordId, newStatus) {
    try {
      await axios.put(
        `${API_URL}/api/tournaments/${tournamentId}/requests/${discordId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      // Update local state without reloading — no grey flash
      setRegistrations((prev) =>
        prev.map((t) => {
          if (String(t.tournamentId) !== String(tournamentId)) return t;
          const prevRequest = t.requests.find((r) => r.discordId === discordId);
          const wasAccepted = prevRequest?.status === "accepted";
          const willBeAccepted = newStatus === "accepted";
          let acceptedDelta = 0;
          if (willBeAccepted && !wasAccepted) acceptedDelta = 1;
          if (!willBeAccepted && wasAccepted) acceptedDelta = -1;
          return {
            ...t,
            acceptedCount: Math.max(0, (t.acceptedCount || 0) + acceptedDelta),
            requests: t.requests.map((r) =>
              r.discordId === discordId ? { ...r, status: newStatus } : r
            ),
          };
        })
      );
    } catch (err) {
      await alert(err.response?.data?.message || `Failed to update request`);
    }
  }

  if (!user) {
    return (
      <>
        <Dialog dialogState={dialogState} />
        <div className="admin-dashboard-page">
        <div className="container">
          <div className="empty-state">
            <FiShield size={48} />
            <h3>Access Denied</h3>
            <p>You must be logged in to view this page</p>
          </div>
        </div>
      </div>
      </>
    );
  }

  if (!isAdmin) {
    return (
      <>
        <Dialog dialogState={dialogState} />
        <div className="admin-dashboard-page">
          <div className="container">
            <div className="empty-state">
              <FiShield size={48} />
              <h3>Access Denied</h3>
              <p>Admin access required</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const totalPending = registrations.reduce(
    (sum, t) => sum + t.requests.filter((r) => r.status === "pending").length,
    0
  );

  return (
    <>
    <Dialog dialogState={dialogState} />
    <div className="admin-dashboard-page">
      <div className="dashboard-header">
        <div className="container">
          <h1 className="page-title font-display">Admin Dashboard</h1>
          <p className="page-subtitle">
            Welcome, <span className="highlight">{user.username}</span>
            {isSuperAdmin && <span className="super-badge">Super Admin</span>}
          </p>
        </div>
      </div>

      <div className="container">
        {/* Tabs */}
        <div className="dashboard-tabs">
          <button
            className={`dash-tab ${activeTab === "registrations" ? "active" : ""}`}
            onClick={() => setActiveTab("registrations")}
          >
            <FiUsers size={18} />
            <span>Registration Requests</span>
            {totalPending > 0 && <span className="tab-badge">{totalPending}</span>}
          </button>
          <button
            className={`dash-tab ${activeTab === "settings" ? "active" : ""}`}
            onClick={() => setActiveTab("settings")}
          >
            <FiLock size={18} />
            <span>Account Settings</span>
          </button>
          {isSuperAdmin && (
            <button
              className={`dash-tab ${activeTab === "admins" ? "active" : ""}`}
              onClick={() => setActiveTab("admins")}
            >
              <FiShield size={18} />
              <span>Manage Admins</span>
            </button>
          )}
        </div>

        <div className="dashboard-grid">

          {/* ===== REGISTRATION REQUESTS TAB ===== */}
          {activeTab === "registrations" && (
            <div className="dashboard-card wide">
              <div className="card-header">
                <div className="header-icon"><FiUsers size={24} /></div>
                <h2>Registration Requests</h2>
                <button onClick={fetchRegistrations} className="btn btn-ghost btn-icon" title="Refresh">
                  <FiRefreshCw size={18} />
                </button>
              </div>

              {/* Filter toolbar */}
              {!regsLoading && registrations.length > 0 && (
                <div className="reg-filter-bar">
                  {["all", "pending", "accepted", "rejected"].map((f) => (
                    <button
                      key={f}
                      className={`reg-filter-btn${regFilter === f ? " active" : ""}`}
                      onClick={() => setRegFilter(f)}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              )}

              {regsLoading ? (
                <div className="loading-container"><div className="spinner" /><p>Loading...</p></div>
              ) : registrations.length === 0 ? (
                <p className="empty-message">No registration requests yet</p>
              ) : (
                registrations.map((t) => {
                  const statusOrder = { pending: 0, accepted: 1, rejected: 2 };
                  const filteredRequests = t.requests
                    .filter((r) => regFilter === "all" || r.status === regFilter)
                    .slice()
                    .sort((a, b) => (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3));

                  if (filteredRequests.length === 0) return null;

                  return (
                    <div key={t.tournamentId} className="reg-tournament-block">
                      <div className="reg-tournament-header">
                        <h3 className="reg-tournament-name">{t.tournamentName}</h3>
                        <div className="reg-tournament-meta">
                          {t.playerCap && (
                            <span className="cap-pill">{t.acceptedCount}/{t.playerCap} accepted</span>
                          )}
                          <span className={`badge ${t.started ? "badge-warning" : t.ended ? "badge-error" : "badge-success"}`}>
                            {t.ended ? "Ended" : t.started ? "In Progress" : "Open"}
                          </span>
                        </div>
                      </div>

                      <table className="reg-table">
                        <thead>
                          <tr>
                            <th>Discord User</th>
                            <th>Requested</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRequests.map((r) => (
                            <tr key={r.discordId}>
                              <td>
                                <div className="user-cell">
                                  <FiUser size={16} />
                                  <span>{r.username}</span>
                                </div>
                              </td>
                              <td className="date-cell">
                                {new Date(r.requestedAt).toLocaleDateString()}
                              </td>
                              <td>
                                <span className={`role-badge ${r.status}`}>{r.status}</span>
                              </td>
                              <td>
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                  {r.status !== "accepted" && (
                                    <button
                                      onClick={() => handleRequestAction(t.tournamentId, r.discordId, "accepted")}
                                      className="btn-action accept"
                                      title="Accept"
                                    >
                                      <FiCheck size={15} />
                                    </button>
                                  )}
                                  {r.status !== "rejected" && (
                                    <button
                                      onClick={() => handleRequestAction(t.tournamentId, r.discordId, "rejected")}
                                      className="btn-action reject"
                                      title="Reject"
                                    >
                                      <FiX size={15} />
                                    </button>
                                  )}
                                  {r.status !== "pending" && (
                                    <button
                                      onClick={() => handleRequestAction(t.tournamentId, r.discordId, "pending")}
                                      className="btn-action pending"
                                      title="Set back to pending"
                                    >
                                      <FiClock size={15} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ===== ACCOUNT SETTINGS TAB ===== */}
          {activeTab === "settings" && (
            <div className="dashboard-card">
              <div className="card-header">
                <div className="header-icon"><FiLock size={24} /></div>
                <h2>Change Password</h2>
              </div>

              {!showPasswordForm ? (
                <button onClick={() => setShowPasswordForm(true)} className="btn btn-secondary btn-block">
                  Update Your Password
                </button>
              ) : (
                <form onSubmit={handleChangePassword} className="form">
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">Change Password</button>
                    <button type="button" onClick={() => { setShowPasswordForm(false); setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" }); }} className="btn btn-ghost">
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* ===== MANAGE ADMINS TAB (Super Admin Only) ===== */}
          {activeTab === "admins" && isSuperAdmin && (
            <div className="dashboard-card wide">
              <div className="card-header">
                <div className="header-icon"><FiShield size={24} /></div>
                <h2>Manage Administrators</h2>
                <button onClick={() => setShowAddForm(!showAddForm)} className="btn btn-primary">
                  <FiUserPlus size={18} />
                  <span>Add Admin</span>
                </button>
              </div>

              {showAddForm && (
                <div className="add-admin-form">
                  <form onSubmit={handleAddAdmin} className="form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Username</label>
                        <input type="text" value={newAdmin.username} onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })} required />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} required minLength={6} />
                      </div>
                      <div className="form-group">
                        <label>Role</label>
                        <select value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}>
                          <option value="admin">Regular Admin</option>
                          <option value="super_admin">Super Admin</option>
                          <option value="staff">Staff (score editor only)</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary">Create Admin</button>
                      <button type="button" onClick={() => { setShowAddForm(false); setNewAdmin({ username: "", password: "", role: "admin" }); }} className="btn btn-ghost">Cancel</button>
                    </div>
                  </form>
                </div>
              )}

              {isLoading ? (
                <div className="loading-container"><div className="spinner" /><p>Loading admins...</p></div>
              ) : (
                <div className="admins-table">
                  {admins.length === 0 ? (
                    <p className="empty-message">No admins found</p>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Role</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {admins.map((admin) => (
                          <tr key={admin._id}>
                            <td><div className="user-cell"><FiUser size={18} /><span>{admin.username}</span></div></td>
                            <td><span className={`role-badge ${admin.role}`}>{admin.role === "super_admin" ? "Super Admin" : admin.role === "staff" ? "Staff" : "Admin"}</span></td>
                            <td className="date-cell">{new Date(admin.createdAt).toLocaleDateString()}</td>
                            <td>
                              {admin.username !== "Melkote2025" && admin.username !== "Freshy2025" && admin.username !== user.username && (
                                <button onClick={() => handleDeleteAdmin(admin.username)} className="btn-icon-danger" title="Delete Admin">
                                  <FiTrash2 size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default AdminDashboard;
